<template>
  <ClienteSideDrawer
    :open="open"
    :title="drawer?.nome || drawerTitle"
    :subtitle="drawerSubtitle"
    :wide="drawerWide"
    :body-class="drawerBodyClass"
    :show-header="drawerShowHeader"
    :aria-label="drawerSubtitle"
    @close="$emit('close')"
  >
    <template v-if="drawer?.tab === 'agendamento'" #icon>
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
    </template>
    <template v-else-if="drawer?.tab === 'financeiro'" #icon>
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
    </template>
    <template v-else-if="drawer?.tab === 'anexos'" #icon>
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>
    </template>

    <div v-if="drawer?.tab === 'historico'" class="cliente-tab-pane cliente-tab-pane--fill cliente-side-drawer-pane">
      <TaskChatPanel
        v-if="drawer?.id"
        :cliente-id="drawer.id"
        :plan-blocked="chatPlanBlocked"
        :plan-gate-title="t('workspaceClienteModal.chat.planLock')"
        :plan-gate-description="t('workspaceClienteModal.chat.planLockDesc')"
        :incoming-bubble-light-text="true"
        class="h-full"
      />
    </div>

    <WhatsAppChatPanel
      v-else-if="drawer?.tab === 'whatsapp' && drawer?.id && showWhatsAppTab"
      ref="waPanelRef"
      entity-type="cliente"
      :entity-id="drawer.id"
      :workspace-id="workspaceId"
      :contact-name="drawer.nome"
      :contact-phone="drawer.telefone"
      :active="open"
      layout="tab"
      column-class="wa-drawer-chat"
      class="cliente-side-drawer-pane cliente-side-drawer-pane--fill"
    />

    <ClienteAgendamentoDrawerPanel
      v-else-if="drawer?.tab === 'agendamento' && drawer?.id"
      :cliente-id="drawer.id"
      :workspace-id="workspaceId"
      :active="open"
      :motivos-agendamento="motivosAgendamento"
    />

    <ClienteFinanceiroDrawerPanel
      v-else-if="drawer?.tab === 'financeiro' && drawer?.id"
      :cliente-id="drawer.id"
      :workspace-id="workspaceId"
      :active="open"
      @updated="$emit('financeiro-updated')"
    />

    <div
      v-else-if="drawer?.tab === 'anexos' && drawer?.id"
      class="cliente-side-drawer-pane cliente-side-drawer-pane--anexos p-0"
    >
      <AnexosSection :cliente-id="drawer.id" icon-only-upload @anexos-updated="() => {}" />
    </div>
  </ClienteSideDrawer>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '@/services/api'
import { usePlanoFeatures } from '@/composables/usePlanoFeatures'
import ClienteSideDrawer from '@/components/cliente/ClienteSideDrawer.vue'
import TaskChatPanel from '@/components/TaskChatPanel.vue'
import WhatsAppChatPanel from '@/components/WhatsAppChatPanel.vue'
import ClienteAgendamentoDrawerPanel from '@/components/cliente/ClienteAgendamentoDrawerPanel.vue'
import ClienteFinanceiroDrawerPanel from '@/components/cliente/ClienteFinanceiroDrawerPanel.vue'
import AnexosSection from '@/components/AnexosSection.vue'
import '@/assets/styles/cliente-modal-theme.css'

const props = defineProps({
  drawer: { type: Object, default: null },
  workspaceId: { type: Number, required: true },
  motivosAgendamento: { type: Array, default: () => [] }
})

defineEmits(['close', 'financeiro-updated'])

const { t } = useI18n()
const planoFeatures = usePlanoFeatures()

const waPanelRef = ref(null)
const whatsAppConnected = ref(false)

const open = computed(() => !!props.drawer?.id)

const showWhatsAppTab = computed(() => whatsAppConnected.value)

const chatPlanBlocked = computed(() => {
  if (planoFeatures.loading.value) return false
  return !planoFeatures.can('servicosHistoricoChat')
})

const drawerTitle = computed(() => {
  const tab = props.drawer?.tab
  if (tab === 'historico') return t('workspaceClienteModal.tabs.historico')
  if (tab === 'whatsapp') return t('workspaceClienteModal.tabs.whatsapp')
  if (tab === 'agendamento') return t('workspaceClienteModal.tabs.agendamento')
  if (tab === 'financeiro') return t('workspaceClienteModal.tabs.financeiro')
  if (tab === 'anexos') return t('workspaceClienteModal.tabs.anexos')
  return ''
})

const drawerSubtitle = computed(() => drawerTitle.value)

const drawerWide = computed(() => ['historico', 'whatsapp'].includes(props.drawer?.tab))

const drawerShowHeader = computed(() => !['historico', 'whatsapp'].includes(props.drawer?.tab))

const drawerBodyClass = computed(() => {
  if (['historico', 'whatsapp', 'agendamento', 'financeiro'].includes(props.drawer?.tab)) {
    return 'cliente-side-drawer-body--flush'
  }
  return ''
})

async function refreshWhatsAppStatus () {
  if (!props.workspaceId) {
    whatsAppConnected.value = false
    return
  }
  try {
    const r = await api.get(`/whatsapp/workspace/${props.workspaceId}`)
    whatsAppConnected.value = String(r.data?.status || '').toUpperCase() === 'CONNECTED'
  } catch {
    whatsAppConnected.value = false
  }
}

watch(
  () => [props.drawer?.tab, props.drawer?.id, open.value],
  async ([tab, id, isOpen]) => {
    if (!isOpen || !id) return
    if (tab === 'whatsapp') {
      await refreshWhatsAppStatus()
      await nextTick()
      await waPanelRef.value?.refreshWhatsAppStatus?.()
      await waPanelRef.value?.loadChatHistory?.()
    }
  }
)

defineExpose({ refreshWhatsAppStatus, whatsAppConnected })

onMounted(async () => {
  await planoFeatures.loadForWorkspace(props.workspaceId)
  await refreshWhatsAppStatus()
})
</script>
